import { setRefreshTokenCallback } from '@/api/middleware/tokenRefresh';
import { AuthContext } from '@/context/AuthContext';
import {
  authenticateUser,
  exchangeTaraResult,
  registerUser,
  registerWithTara,
  revokeToken,
  startTaraLogin,
  verifyRegistration,
} from '@/features/auth/authService';
import { clearProfile, getProfile } from '@/features/profile/profileActions';
import { useDispatch } from '@/store/utils';
import type { LoginResponse, TaraExchangeResult } from '@/types/auth';
import type { CreateCustomerProfileRequestBodyDTO } from '@/types/dto/agent';
import {
  clearTokens,
  getTokenExpiry,
  getTokensRef,
  setAuthData,
  setTaraReturnPath,
} from '@/utils/auth';
import { GTM_EVENT, trackCustomEvent } from '@/utils/gtm';
import { sha256LowercaseHex } from '@/utils/hash';
import type { LoginValues } from '@/utils/zodSchema';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [tokenUpdateTrigger, setTokenUpdateTrigger] = useState(0);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleLoginSuccess = useCallback(
    async (response: LoginResponse) => {
      setAuthData(response.tokens_ref, response.expires_in);
      const profile = await dispatch(getProfile()).unwrap();
      setTokenUpdateTrigger((prev) => prev + 1);

      // Tagging plan (BR-57983). user_profil, new_customer and user_orders
      // are omitted until the profile API exposes a b2b/b2c discriminator and
      // an order count (plan Q4/Q5).
      trackCustomEvent(GTM_EVENT.LOGIN, {
        email_hashed: profile?.email
          ? await sha256LowercaseHex(profile.email)
          : undefined,
        user_id: profile?.id,
      });
    },
    [dispatch]
  );

  const logout = useCallback(async () => {
    try {
      const tokensRef = getTokensRef();

      if (tokensRef) {
        await revokeToken({
          tokens_ref: tokensRef,
        });
      }
    } finally {
      clearTokens();
      setTaraReturnPath(null);
      dispatch(clearProfile());
      // Offer search — not /login. With TARA enabled, /login immediately hands
      // off to the external IdP, which is wrong after an intentional logout
      // (BR-56868).
      navigate('/purchase');
    }
  }, [navigate, dispatch]);

  const refreshAccessToken = useCallback(
    async (tokensRef: string) => {
      if (!tokensRef) {
        throw new Error('No tokens ref available');
      }

      try {
        const response = await authenticateUser({
          grant_type: 'refresh_token',
          tokens_ref: tokensRef,
        });

        if (!response.tokens_ref || !response.expires_in) {
          throw new Error('Invalid token response');
        }

        setAuthData(response.tokens_ref, response.expires_in);
        setTokenUpdateTrigger((prev) => prev + 1);
      } catch (error) {
        await logout();
        throw error;
      }
    },
    [logout]
  );

  const loginWithPassword = useCallback(
    async (values: LoginValues): Promise<LoginResponse> => {
      const response = await authenticateUser({
        grant_type: 'password',
        username: values.email,
        password: values.password,
      });

      await handleLoginSuccess(response);
      return response;
    },
    [handleLoginSuccess]
  );

  const loginWithTara = useCallback(
    async (loginResultToken: string): Promise<TaraExchangeResult> => {
      const result = await exchangeTaraResult(loginResultToken);

      if (result.kind === 'authenticated') {
        await handleLoginSuccess(result.response);
      }

      return result;
    },
    [handleLoginSuccess]
  );

  const registerWithPassword = useCallback(
    async (values: CreateCustomerProfileRequestBodyDTO): Promise<void> => {
      await registerUser({
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        birthDate: values.birthDate,
        genderId: values.genderId,
        phoneNumber: values.phoneNumber,
        password: values.password,
        consents: values.consents,
        isMemberOfLoyaltyProgram: false,
      });
    },
    []
  );

  useEffect(() => {
    setIsLoading(false);
  }, []);

  const tokensRef = getTokensRef();
  const currentTokenExpiry = getTokenExpiry();

  useEffect(() => {
    if (!tokensRef || !currentTokenExpiry) return;

    const timeUntilExpiry = Number(currentTokenExpiry) - Date.now();
    // Refresh at 95% of token lifetime to minimize window where token could expire
    // between scheduled refreshes while still providing buffer for network/processing delays
    const refreshDelay = timeUntilExpiry * 0.95;

    const refreshTimeout = setTimeout(async () => {
      await refreshAccessToken(tokensRef);
    }, refreshDelay);

    return () => clearTimeout(refreshTimeout);
  }, [refreshAccessToken, tokensRef, currentTokenExpiry, tokenUpdateTrigger]);

  // Register refresh callback for 401 error interceptor
  useEffect(() => {
    const tokensRef = getTokensRef();

    if (tokensRef) {
      // Register callback so interceptor can trigger refresh on 401 errors
      setRefreshTokenCallback(() => refreshAccessToken(tokensRef));
    }

    return () => {
      // Clean up on unmount
      setRefreshTokenCallback(() => Promise.resolve());
    };
  }, [refreshAccessToken]);

  const value = {
    isAuthenticated: !!getTokensRef(),
    isLoading,
    logout,
    loginWithPassword,
    registerWithPassword,
    registerWithTara,
    verifyRegistration,
    startTaraLogin,
    loginWithTara,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
